import { forwardRef, useMemo } from 'react';
import type { MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent } from 'react';
import { colors } from '@/lib/ui-tokens';
import {
  centerOf,
  cornerRadius,
  type HandleId,
  type Rect,
  type Scene,
  type SceneElement,
} from '../lib/scene';

export interface View {
  x: number;
  y: number;
  zoom: number;
}

export type CanvasTarget =
  | { kind: 'canvas' }
  | { kind: 'element'; id: string }
  | { kind: 'handle'; id: string; handle: HandleId };

interface MapCanvasProps {
  scene: Scene;
  mode: 'live' | 'edit';
  view: View;
  grid: number;
  showGrid: boolean;
  selection: readonly string[];
  marquee?: Rect | null;
  /** Fill for one element — live mode passes the §7 state colour. */
  fillFor: (element: SceneElement) => string;
  labelFor: (element: SceneElement) => string;
  /** Extra line for the SVG tooltip, e.g. the live table state. */
  detailFor?: (element: SceneElement) => string | null;
  /**
   * §38 — a table or resource element that is not standing for a real entity.
   * `warning` is "never linked", `danger` is "the entity it pointed at is gone":
   * different mistakes, so they must not look the same.
   */
  flagFor?: (element: SceneElement) => { tone: 'warning' | 'danger'; title: string } | null;
  onCanvasPointerDown?: (event: ReactPointerEvent<SVGSVGElement>, target: CanvasTarget) => void;
  /**
   * Right-click. The manager works the floor from the floor, so the actions for
   * a table belong on the table itself, not on a panel somewhere else.
   */
  onCanvasContextMenu?: (event: ReactMouseEvent<SVGSVGElement>, target: CanvasTarget) => void;
  onCanvasPointerMove?: (event: ReactPointerEvent<SVGSVGElement>) => void;
  onCanvasPointerUp?: (event: ReactPointerEvent<SVGSVGElement>) => void;
  ariaLabel: string;
  className?: string;
}

const RESIZE_HANDLES: readonly HandleId[] = ['nw', 'ne', 'se', 'sw'];

function handlePosition(element: SceneElement, handle: HandleId, gap: number) {
  switch (handle) {
    case 'nw':
      return { x: element.x, y: element.y };
    case 'ne':
      return { x: element.x + element.width, y: element.y };
    case 'se':
      return { x: element.x + element.width, y: element.y + element.height };
    case 'sw':
      return { x: element.x, y: element.y + element.height };
    case 'rotate':
      return { x: element.x + element.width / 2, y: element.y - gap };
  }
}

/** Reads the hit target back off the DOM, so one listener serves every element. */
function resolveTarget(event: { target: EventTarget | null }): CanvasTarget {
  const node = (event.target as Element | null)?.closest?.('[data-map-target]') ?? null;
  const kind = node?.getAttribute('data-map-target');
  const id = node?.getAttribute('data-id') ?? '';
  if (kind === 'handle') {
    return { kind: 'handle', id, handle: node?.getAttribute('data-handle') as HandleId };
  }
  if (kind === 'element' && id) return { kind: 'element', id };
  return { kind: 'canvas' };
}

/**
 * The one renderer both modes share (§5.2 asks for edit mode and live mode over
 * the same scene, not two drifting views). Only the fill callback, the pointer
 * handlers and the selection chrome differ.
 */
export const MapCanvas = forwardRef<SVGSVGElement, MapCanvasProps>(function MapCanvas(
  {
    scene,
    mode,
    view,
    grid,
    showGrid,
    selection,
    marquee,
    fillFor,
    labelFor,
    detailFor,
    flagFor,
    onCanvasPointerDown,
    onCanvasContextMenu,
    onCanvasPointerMove,
    onCanvasPointerUp,
    ariaLabel,
    className,
  },
  ref,
) {
  const editing = mode === 'edit';
  const selected = useMemo(() => new Set(selection), [selection]);

  const ordered = useMemo(
    () => [...scene.elements].sort((a, b) => a.zIndex - b.zIndex),
    [scene.elements],
  );

  // Chrome keeps a constant on-screen size however far the view is zoomed in.
  const px = 1 / view.zoom;
  const handleSize = 12 * px;
  const gridId = `map-grid-${scene.floorId}`;

  const singleSelection = selection.length === 1 ? ordered.find((el) => el.id === selection[0]) : null;

  return (
    <svg
      ref={ref}
      viewBox={`${view.x} ${view.y} ${scene.width / view.zoom} ${scene.height / view.zoom}`}
      className={className}
      style={{ aspectRatio: `${scene.width} / ${scene.height}`, touchAction: 'none' }}
      role={editing ? 'application' : 'img'}
      aria-label={ariaLabel}
      onPointerDown={(event) => onCanvasPointerDown?.(event, resolveTarget(event))}
      onContextMenu={
        onCanvasContextMenu
          ? (event) => onCanvasContextMenu(event, resolveTarget(event))
          : undefined
      }
      onPointerMove={onCanvasPointerMove}
      onPointerUp={onCanvasPointerUp}
      onPointerCancel={onCanvasPointerUp}
    >
      <defs>
        <pattern id={gridId} width={grid} height={grid} patternUnits="userSpaceOnUse">
          <path
            d={`M ${grid} 0 L 0 0 0 ${grid}`}
            fill="none"
            stroke="var(--border)"
            strokeWidth={px}
          />
        </pattern>
      </defs>

      <rect
        x={view.x}
        y={view.y}
        width={scene.width / view.zoom}
        height={scene.height / view.zoom}
        fill="var(--map-ground)"
      />
      {showGrid && (
        <rect
          x={view.x}
          y={view.y}
          width={scene.width / view.zoom}
          height={scene.height / view.zoom}
          fill={`url(#${gridId})`}
        />
      )}
      <rect
        x={0}
        y={0}
        width={scene.width}
        height={scene.height}
        fill="none"
        stroke="var(--border-strong)"
        strokeWidth={2 * px}
      />

      {ordered.map((element) => {
        if (!element.visible && !editing) return null;
        const isSelected = editing && selected.has(element.id);
        const label = labelFor(element);
        const detail = detailFor?.(element) ?? null;
        const flag = flagFor?.(element) ?? null;
        const center = centerOf(element);
        const radius = cornerRadius(element.elementType);
        const isText = element.elementType === 'TEXT';

        return (
          <g
            key={element.id}
            data-map-target="element"
            data-id={element.id}
            transform={`rotate(${element.rotation} ${center.x} ${center.y})`}
            opacity={element.visible ? 1 : 0.35}
            style={{ cursor: editing ? (element.locked ? 'not-allowed' : 'move') : 'pointer' }}
          >
            <title>
              {[label, detail, flag?.title].filter(Boolean).join(' — ')}
            </title>
            <rect
              x={element.x}
              y={element.y}
              width={element.width}
              height={element.height}
              rx={radius}
              ry={radius}
              fill={isText ? 'none' : fillFor(element)}
              stroke={
                isSelected
                  ? 'var(--accent)'
                  : element.locked && editing
                    ? 'var(--border-strong)'
                    : 'var(--border)'
              }
              strokeWidth={(isSelected ? 2.5 : 1) * px}
              strokeDasharray={editing && (element.locked || !element.visible) ? `${6 * px}` : undefined}
            />

            {label && element.width > 34 && element.height > 16 && (
              <text
                x={center.x}
                y={center.y}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={Math.max(10, Math.min(16, element.height / 3.5))}
                fill={colors.neutral[800]}
                pointerEvents="none"
              >
                {label}
              </text>
            )}

            {/* §38 — a table or resource element with no entity behind it is a
                setup mistake, so the editor flags it before publish does. A
                dangling link gets a ring as well as a dot: the shape is fine,
                the thing it stood for is gone. */}
            {flag && (
              <>
                {flag.tone === 'danger' && (
                  <rect
                    x={element.x}
                    y={element.y}
                    width={element.width}
                    height={element.height}
                    rx={radius}
                    ry={radius}
                    fill="none"
                    stroke={colors.danger}
                    strokeWidth={2.5 * px}
                    strokeDasharray={`${5 * px} ${3 * px}`}
                    pointerEvents="none"
                  />
                )}
                <circle
                  cx={element.x + element.width - 7 * px}
                  cy={element.y + 7 * px}
                  r={4.5 * px}
                  fill={flag.tone === 'danger' ? colors.danger : colors.warning}
                  stroke="var(--map-ground)"
                  strokeWidth={px}
                  pointerEvents="none"
                />
              </>
            )}
          </g>
        );
      })}

      {editing && singleSelection && (
        <g
          transform={`rotate(${singleSelection.rotation} ${centerOf(singleSelection).x} ${centerOf(singleSelection).y})`}
        >
          {!singleSelection.locked && (
            <>
              <line
                x1={singleSelection.x + singleSelection.width / 2}
                y1={singleSelection.y}
                x2={singleSelection.x + singleSelection.width / 2}
                y2={singleSelection.y - 28 * px}
                stroke="var(--accent)"
                strokeWidth={1.5 * px}
              />
              <circle
                data-map-target="handle"
                data-handle="rotate"
                data-id={singleSelection.id}
                cx={handlePosition(singleSelection, 'rotate', 28 * px).x}
                cy={handlePosition(singleSelection, 'rotate', 28 * px).y}
                r={handleSize / 1.6}
                fill="var(--accent)"
                style={{ cursor: 'grab' }}
              />
              {RESIZE_HANDLES.map((handle) => {
                const point = handlePosition(singleSelection, handle, 0);
                return (
                  <rect
                    key={handle}
                    data-map-target="handle"
                    data-handle={handle}
                    data-id={singleSelection.id}
                    x={point.x - handleSize / 2}
                    y={point.y - handleSize / 2}
                    width={handleSize}
                    height={handleSize}
                    fill="var(--card)"
                    stroke="var(--accent)"
                    strokeWidth={1.5 * px}
                    style={{ cursor: handle === 'nw' || handle === 'se' ? 'nwse-resize' : 'nesw-resize' }}
                  />
                );
              })}
            </>
          )}
        </g>
      )}

      {editing &&
        selection.length > 1 &&
        ordered
          .filter((element) => selected.has(element.id))
          .map((element) => (
            <rect
              key={`sel-${element.id}`}
              x={element.x}
              y={element.y}
              width={element.width}
              height={element.height}
              transform={`rotate(${element.rotation} ${centerOf(element).x} ${centerOf(element).y})`}
              fill="none"
              stroke="var(--accent)"
              strokeWidth={2 * px}
              pointerEvents="none"
            />
          ))}

      {marquee && (
        <rect
          x={marquee.x}
          y={marquee.y}
          width={marquee.width}
          height={marquee.height}
          fill="var(--accent)"
          fillOpacity={0.35}
          stroke="var(--accent)"
          strokeWidth={px}
          pointerEvents="none"
        />
      )}
    </svg>
  );
});
